import { Avatar, Box, Typography } from "@mui/material";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useNavigate } from "react-router-dom";
import { Product } from './MobileMagazines';

interface MagazinesCarouselProps {
    magazinesList: any[];
    totalRecords: any;
    magazineLang?: any;
    magazineLanguageId?: any;
    itemType?: string;
    t: (key: string) => string;
}

const MagazinesCarousel: React.FC<MagazinesCarouselProps> = ({ magazinesList, totalRecords, magazineLang, magazineLanguageId, itemType, t }) => {
    const navigate = useNavigate();

    const isSimpleLangLabel = (rawLang: any): boolean => {
        const lang = String(rawLang || '').toLowerCase();
        return lang === 'english' || lang === 'en' || lang === 'hindi' || lang === 'hi';
    };

    // Normalize language values (english/en → english, hindi/hi → hindi)
    const getLangLabel = (rawLang: any): string => {
        if (!rawLang && rawLang !== 0) return '';
        const lang = String(rawLang).toLowerCase();
        if (lang === 'english' || lang === 'en' || lang.includes('english') || lang.includes('angrezi')) return 'english';
        if (lang === 'hindi' || lang === 'hi' || lang.includes('hindi') || lang.includes('hin') || lang.includes('हिं') || lang.includes('हिन')) return 'hindi';
        return '';
    };

    // Resolve english/hindi label + API language id for View All navigation
    const resolveViewAllLanguage = () => {
        const logdetails: string[] = [];

        let langLabel = getLangLabel(magazineLang);

        if (!langLabel) {
            for (const magazine of magazinesList) {
                const inferred = getLangLabel(
                    magazine?.monthlyMagazineLanguage ||
                    magazine?.magazineLanguage ||
                    magazine?.magazineLangauge
                );
                if (inferred) {
                    langLabel = inferred;
                    break;
                }
            }
        }

        const languageId =
            magazineLanguageId ||
            (!isSimpleLangLabel(magazineLang) ? magazineLang : '') ||
            magazinesList?.[0]?.monthlyMagazineLanguageId ||
            magazinesList?.find((m) => m?.monthlyMagazineLanguageId)?.monthlyMagazineLanguageId ||
            (langLabel === 'english' || langLabel === 'hindi' ? langLabel : '');

        if (!langLabel && languageId) {
            langLabel = getLangLabel(languageId);
        }
        if (!langLabel) {
            langLabel = 'hindi';
        }

        logdetails.push(`resolved lang=${langLabel}`);
        logdetails.push(`resolved languageId=${languageId}`);

        return { langLabel, languageId, logdetails };
    };

    // Navigate to Geet Govind grid list (MobileBookPage via /books), same as Latest section View All.
    const handleViewAllClick = () => {
        const logdetails: string[] = [];
        const { langLabel, languageId, logdetails: resolveLogs } = resolveViewAllLanguage();

        logdetails.push(`view_all_click totalRecords=${totalRecords}`);
        logdetails.push(...resolveLogs);

        navigate('/books', {
            state: {
                type: 'geetGovind',
                geetGovindLang: langLabel,
                magazineLang: languageId,
                magazineLanguage: languageId,
            }
        });

        console.log('[MagazinesCarousel]', logdetails.join(' | '));
    };

    // Navigate to same Geet Govind page with magazine id in state (same path pattern as hub).
    // Keep /magazines/geetgovind so the existing list page remounts with selection state.
    const handleMagazineClick = (magazine: any) => {
        const logdetails: string[] = [];
        const rawLang =
            magazine?.magazineLanguage ||
            magazine?.magazineLangauge ||
            magazine?.monthlyMagazineLanguage ||
            magazineLang;
        const langLabel =
            getLangLabel(rawLang) ||
            getLangLabel(magazine?.monthlyMagazineLanguage) ||
            (isSimpleLangLabel(magazineLang) ? getLangLabel(magazineLang) : '') ||
            'hindi';
        const langForApi =
            magazine?.monthlyMagazineLanguageId ||
            magazineLanguageId ||
            (!isSimpleLangLabel(magazineLang) ? magazineLang : langLabel);
        const magazineId = magazine?.id;
        const slug = magazine?.slug || magazineId;

        logdetails.push(`carousel_click magazineId=${magazineId}`);
        logdetails.push(`lang=${langLabel}`);
        logdetails.push(`slug=${slug}`);

        navigate('/magazines/geetgovind', {
            state: {
                productType: Product.MonthlyMagazine,
                geetGovindId: magazineId,
                magzineId: magazineId,
                geetGovindLang: langLabel,
                magazineLanguage: langForApi,
                bookName: magazine?.name,
                slug,
            }
        });

        console.log('[MagazinesCarousel]', logdetails.join(' | '));
    };

    return (
        <Box sx={{
            mt: 2,
            backgroundColor: 'white',
            margin: '5px -2px 1px -2px',
            padding: '12px',
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography sx={{ fontWeight: 700, fontFamily: 'Inter, sans-serif', color: 'red', fontSize: '11px' }}>
                    {t('magazine_view_all_tr')}
                </Typography>
                <div
                    onClick={handleViewAllClick}
                    style={{
                        backgroundColor: "#f97316",
                        padding: '5px 10px',
                        height: 30,
                        borderRadius: 3,
                        color: "white",
                        whiteSpace: 'nowrap',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontFamily: 'chanakyaUni',
                        cursor: 'pointer',
                        marginLeft: '10px',
                        fontSize: '14px'
                    }}
                >
                    {t('view_all_tr')} {totalRecords} {itemType || t('itemtype_books_tr')} {t('view_all_suffix_tr')}
                </div>
            </Box>
            <Box sx={{
                display: 'flex',
                overflowX: 'auto',
                gap: '1px',
                pb: 1,
                scrollbarWidth: "none",
                msOverflowStyle: "none"
            }}>
                {magazinesList.map((magazine: any, index: number) => (
                    <Box key={magazine?.id || index} sx={{
                        flexShrink: 0,
                        width: '120px',
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        cursor: 'pointer',
                    }}
                        onClick={() => handleMagazineClick(magazine)}
                    >
                        <Box sx={{
                            width: '100px',
                            height: '150px',
                            overflow: 'hidden',
                            mb: 1,
                        }}>
                            <img
                                src={magazine?.monthlyMagazineThumbPath || "https://placehold.co/100x150/E0E0E0/333333?text=No+Image"}
                                alt={magazine?.name || "Magazine"}
                                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'none' }}
                                onError={(e: any) => { e.target.onerror = null; e.target.src = "https://placehold.co/100x150/E0E0E0/333333?text=No+Image"; }}
                            />
                        </Box>
                        <Typography variant="body2" sx={{
                            fontSize: '0.60rem',
                            fontWeight: 500,
                            color: '#555',
                            fontFamily: 'Inter, sans-serif',
                            whiteSpace: 'normal',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                        }}>
                            {magazine?.name || "Untitled Magazine"}
                        </Typography>
                    </Box>
                ))}
                <Box sx={{
                    flexShrink: 0,
                    width: '120px',
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                }}
                    onClick={handleViewAllClick}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '4px',
                            textAlign: 'center',
                        }}
                    >
                        <Avatar sx={{ bgcolor: '#f97316', width: 35, height: 35 }}>
                            <ArrowForwardIosIcon sx={{ fontSize: 16 }} onClick={handleViewAllClick} />
                        </Avatar>
                        <Typography className='mt-2' style={{ fontSize: '14px', color: "#f97316" }}>{t('view_all_tr')}</Typography>
                    </div>
                </Box>
            </Box>
        </Box>
    );
};
export default MagazinesCarousel
